import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users as UsersIcon, Search } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", dob: "", pronouns: "", diagnosis: "", presenting_concerns: "", risk_level: "low", language: "en" });

  const load = () => api.get("/clients").then(({ data }) => setClients(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setModal(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", dob: "", pronouns: "", diagnosis: "", presenting_concerns: "", risk_level: "low", language: "en" });
      load();
    } catch (err) {
      toast.error("Could not add client");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const filtered = clients.filter(c =>
    `${c.first_name} ${c.last_name} ${c.diagnosis || ""} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Page
      title="Clients"
      subtitle="All client records, linked to notes, plans, and outcomes."
      actions={<PrimaryButton onClick={() => setModal(true)} testid="add-client-btn"><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add Client</span></PrimaryButton>}
    >
      <Card delay={0.05} className="mb-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--mf-text-muted)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, diagnosis, or email…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl outline-none text-sm"
            style={{ background: "var(--mf-muted)", color: "var(--mf-text)" }}
            data-testid="clients-search"
          />
        </div>
      </Card>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map((i) => <div key={i} className="mf-skeleton h-32"></div>)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No clients yet"
          description="Start by adding your first client. Their records will be linked to all modules automatically."
          action={<PrimaryButton onClick={() => setModal(true)} testid="empty-add-client-btn">Add a client</PrimaryButton>}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c, i) => (
            <Link key={c.id} to={`/clients/${c.id}`} className="mf-card p-6 mf-fade-in block" style={{ animationDelay: `${Math.min(i * 0.05, 0.3)}s` }} data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-medium" style={{ background: "var(--mf-primary)" }}>
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <div>
                    <div className="font-serif text-lg">{c.first_name} {c.last_name}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{c.pronouns || "—"}</div>
                  </div>
                </div>
                <Pill variant={c.risk_level === "high" ? "danger" : c.risk_level === "moderate" ? "warn" : "success"}>{c.risk_level}</Pill>
              </div>
              <div className="text-sm mb-1" style={{ color: "var(--mf-text)" }}>{c.diagnosis || "No diagnosis on file"}</div>
              <div className="text-xs line-clamp-2" style={{ color: "var(--mf-text-muted)" }}>{c.presenting_concerns || "No presenting concerns recorded"}</div>
            </Link>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setModal(false)}>
          <div className="mf-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-4">Add Client</h2>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="First name" value={form.first_name} onChange={set("first_name")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-firstname" />
                <input required placeholder="Last name" value={form.last_name} onChange={set("last_name")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-lastname" />
              </div>
              <input placeholder="Email" type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-email" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Phone" value={form.phone} onChange={set("phone")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-phone" />
                <input placeholder="Pronouns" value={form.pronouns} onChange={set("pronouns")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-pronouns" />
              </div>
              <DatePicker placeholder="Date of birth" value={form.dob} onChange={set("dob")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-dob" />
              <input placeholder="Diagnosis (e.g. F33.1 Major Depressive Disorder)" value={form.diagnosis} onChange={set("diagnosis")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-diagnosis" />
              <textarea placeholder="Presenting concerns" value={form.presenting_concerns} onChange={set("presenting_concerns")} rows={3} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="new-client-concerns"></textarea>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.risk_level} onChange={set("risk_level")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-risk">
                  <option value="low">Low risk</option>
                  <option value="moderate">Moderate risk</option>
                  <option value="high">High risk</option>
                </select>
                <select value={form.language} onChange={set("language")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-client-language">
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="zh">Mandarin</option>
                  <option value="fr">French</option>
                  <option value="ar">Arabic</option>
                  <option value="pt">Portuguese</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm rounded-full" data-testid="cancel-add-client">Cancel</button>
                <PrimaryButton type="submit" testid="submit-add-client">Add Client</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}
